/**
 * Sync Manager - Handles syncing offline changes with server
 * 
 * Implements a queue-based sync system with retry logic.
 */

import { offlineDB, isClient, isOnline, type SyncOperation } from './db'

// ============================================
// Add to Sync Queue
// ============================================

export async function addToSyncQueue(
    entity: SyncOperation['entity'],
    entityId: string,
    action: SyncOperation['action'],
    payload: any
): Promise<void> {
    if (!isClient()) return

    await offlineDB.syncQueue.add({
        entity,
        entityId,
        action,
        payload,
        createdAt: new Date(),
        status: 'pending',
        retryCount: 0
    })

    // If online, trigger immediate sync
    if (isOnline()) {
        processSyncQueue()
    }
}

// ============================================
// Process Sync Queue
// ============================================

let isSyncing = false

export async function processSyncQueue(): Promise<{ synced: number, failed: number }> {
    if (!isClient() || !isOnline() || isSyncing) {
        return { synced: 0, failed: 0 }
    }

    isSyncing = true
    let synced = 0
    let failed = 0

    try {
        // Get pending operations in order
        const pending = await offlineDB.syncQueue
            .where('status')
            .equals('pending')
            .sortBy('createdAt')

        for (const operation of pending) {
            try {
                // Mark as syncing
                await offlineDB.syncQueue.update(operation.id!, { status: 'syncing' })

                // Execute sync
                const success = await executeSyncOperation(operation)

                if (success) {
                    // Remove from queue on success
                    await offlineDB.syncQueue.delete(operation.id!)
                    synced++

                    // Update local record sync status
                    await updateLocalSyncStatus(operation.entity, operation.entityId, 'synced')
                } else {
                    // Mark as failed and increment retry
                    await offlineDB.syncQueue.update(operation.id!, {
                        status: 'failed',
                        retryCount: operation.retryCount + 1
                    })
                    failed++
                }
            } catch (error) {
                console.error('[Sync] Operation failed:', error)
                await offlineDB.syncQueue.update(operation.id!, {
                    status: 'failed',
                    retryCount: operation.retryCount + 1,
                    errorMessage: error instanceof Error ? error.message : 'Unknown error'
                })
                failed++
            }
        }

        // Update sync state
        await offlineDB.syncState.put({
            entity: 'lastSync',
            lastSyncAt: new Date(),
            syncInProgress: false
        })

    } finally {
        isSyncing = false
    }

    return { synced, failed }
}

// ============================================
// Execute Individual Sync Operation
// ============================================

async function executeSyncOperation(operation: SyncOperation): Promise<boolean> {
    const { entity, entityId, action, payload } = operation

    // Build API endpoint
    const endpoints: Record<SyncOperation['entity'], string> = {
        booking: '/api/bookings',
        passenger: `/api/bookings/${payload.bookingId}/passengers`,
        itinerary: `/api/bookings/${payload.bookingId}/itinerary`,
        confirmation: `/api/bookings/${payload.bookingId}/confirmations`
    }

    const baseUrl = endpoints[entity]
    let url = baseUrl
    let method = 'POST'

    switch (action) {
        case 'create':
            method = 'POST'
            break
        case 'update':
            method = 'PATCH'
            url = entity === 'booking' ? `${baseUrl}/${entityId}` : baseUrl
            break
        case 'delete':
            method = 'DELETE'
            url = entity === 'booking' ? `${baseUrl}/${entityId}` : baseUrl
            break
    }

    const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    })

    return response.ok
}

// ============================================
// Update Local Sync Status
// ============================================

async function updateLocalSyncStatus(
    entity: SyncOperation['entity'],
    entityId: string,
    status: 'synced' | 'pending' | 'conflict'
): Promise<void> {
    const tables = {
        booking: offlineDB.bookings,
        passenger: offlineDB.passengers,
        itinerary: offlineDB.itineraryDays,
        confirmation: offlineDB.confirmations
    }

    const table = tables[entity]
    if (table) {
        await table.update(entityId, { _syncStatus: status })
    }
}

// ============================================
// Retry Failed Operations
// ============================================

export async function retryFailedOperations(): Promise<void> {
    if (!isClient()) return

    // Reset failed operations to pending (max 3 retries)
    await offlineDB.syncQueue
        .where('status')
        .equals('failed')
        .filter(op => op.retryCount < 3)
        .modify({ status: 'pending' })

    // Trigger sync
    processSyncQueue()
}

// ============================================
// Clear Failed Operations
// ============================================

export async function clearFailedOperations(): Promise<number> {
    if (!isClient()) return 0

    const failed = await offlineDB.syncQueue.where('status').equals('failed').toArray()
    await offlineDB.syncQueue.where('status').equals('failed').delete()
    return failed.length
}

// ============================================
// Network Status Listener
// ============================================

export function initSyncOnReconnect(): void {
    if (!isClient()) return

    window.addEventListener('online', () => {
        processSyncQueue()
    })
}
